import { useMemo, useState } from "react";
import { useDebounced } from "@/lib/use-debounced";

// Die Kennziffer einer Ausschreibung (`Listing.reference`): `JJJJ-NNNN`, das Jahr der Anlage und eine
// je Jahr fortlaufende, mindestens vierstellige Nummer, etwa `2026-0042`. genesis vergibt sie und
// findet eine Ausschreibung darüber nur exakt (`reference: {eq: …}`).
//
// Die Suche nimmt die Eingabe so, wie Menschen eine Kennziffer weitergeben: aus einer E-Mail kopiert
// ("Kennziffer: 2026-0042", "#2026-0042"), am Telefon diktiert ("2026 0042") oder aus einem Text, in dem
// die Textverarbeitung den Bindestrich durch einen typografischen Strich ersetzt hat. Text drumherum,
// Leerzeichen und die Art des Strichs zählen deshalb nicht. Eine verkürzte Nummer ("2026-42") ergänzt
// sie bewusst NICHT: die Suche läuft beim Tippen, und auf dem Weg zu "2026-0042" fände "2026-4" sonst
// schon eine ganz andere Ausschreibung, 2026-0004.

// Bindestrich, die typografischen Striche U+2010 bis U+2015 und das Minuszeichen U+2212.
const REFERENCE = /(?<!\d)(\d{4})\s*[-\u2010-\u2015\u2212]?\s*(\d{4,})(?!\d)/;

/** Die Kennziffer in der Schreibweise von genesis, oder null, wenn in der Eingabe keine steht. */
export function parseListingReference(input: string): string | null {
  const match = REFERENCE.exec(input);
  return match ? `${match[1]}-${match[2]}` : null;
}

export interface ReferenceSearchState {
  /** Was im Feld steht, Tastendruck für Tastendruck. */
  term: string;
  setTerm: (next: string) => void;
  /** Es wird gesucht: nach der Pause beim Tippen steht etwas im Feld. */
  active: boolean;
  /** Die gesuchte Kennziffer; null ohne Suche oder wenn die Eingabe keine Kennziffer enthält. */
  reference: string | null;
  /**
   * Gesucht wird, aber nach nichts, was eine Kennziffer sein kann. Dann fragt die Seite genesis gar
   * nicht erst, sondern zeigt gleich, dass es keine solche Ausschreibung gibt.
   */
  impossible: boolean;
  /** Die Bedingung für `ListingFilterInput`; null ohne gesuchte Kennziffer. */
  where: { reference: { eq: string } } | null;
}

/** Das Suchfeld der Listen: die Eingabe, entprellt, und was davon als Filter an genesis geht. */
export function useListingReferenceSearch(): ReferenceSearchState {
  const [term, setTerm] = useState("");
  const settled = useDebounced(term).trim();
  const reference = settled ? parseListingReference(settled) : null;
  // Stabil, solange dieselbe Kennziffer gesucht wird: die Seiten bauen daraus ihre Abfrage, und ein
  // neues Objekt je Tastendruck setzte das Blättern zurück.
  const where = useMemo(() => (reference ? { reference: { eq: reference } } : null), [reference]);
  return { term, setTerm, active: settled !== "", reference, impossible: settled !== "" && !reference, where };
}
